import React, { useState, useCallback } from "react";
import { UnifiedSearchBar } from "../components/UnifiedSearchBar";
import { ProblemList } from "../components/ProblemList";
import { Pagination } from "../components/Pagination";
import { PageSizeSelector } from "../components/PageSizeSelector";
import { SearchGenerationModal, SearchGenerationModalState } from "../components/SearchGenerationModal";
import { AIPrepLogoIcon } from "../components/CustomIcons";
import { apiService, Problem, ProblemStats } from "../services/api";

export const ProblemsTab: React.FC = () => {
  const [problems, setProblems] = useState<Problem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchStatus, setSearchStatus] = useState<string>("");

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [filters, setFilters] = useState({
    difficulty: [] as string[],
    tags: [] as string[],
    company: "" as string,
  });

  // Track the current search query to determine if stats should show filtered results
  const [currentSearchQuery, setCurrentSearchQuery] = useState<string>("");

  const [stats, setStats] = useState<ProblemStats | null>(null);
  const [, setAvailableTags] = useState<string[]>([]);
  
  // Modal state for search/generation progress
  const [modalState, setModalState] = useState<SearchGenerationModalState>({
    phase: 'searching',
    query: '',
    difficulty: undefined  // Let backend determine appropriate difficulty
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Calculate current filtered stats based on pagination and filters
  const currentStats = React.useMemo(() => {
    if (!stats) return null;
    
    // Determine if we should show filtered stats
    const hasCompanyFilter = filters.company !== "";
    const hasSearchQuery = currentSearchQuery.trim() !== "";
    const hasDifficultyFilter = filters.difficulty.length > 0;
    const hasTagsFilter = filters.tags.length > 0;
    const hasAnyFilter = hasCompanyFilter || hasSearchQuery || hasDifficultyFilter || hasTagsFilter;
    
    // If no filters are active, show total stats
    if (!hasAnyFilter || pagination.total === stats.totalProblems) {
      return {
        totalProblems: stats.totalProblems,
        difficultyStats: stats.difficultyStats,
      };
    }
    
    // If filters are active, show filtered stats
    // Use pagination.total for the filtered count
    const currentTotal = pagination.total;
    
    // Calculate difficulty breakdown from the current page of problems
    // This gives us the actual distribution in the current results
    const currentPageDifficulty = problems.reduce((acc, problem) => {
      acc[problem.difficulty] = (acc[problem.difficulty] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    // Estimate total difficulty distribution based on current page
    const currentPageTotal = problems.length;
    const estimatedDifficulty = {
      Easy: 0,
      Medium: 0,
      Hard: 0,
    };
    
    if (currentPageTotal > 0) {
      // Calculate ratios from current page and apply to total
      const easyRatio = (currentPageDifficulty.Easy || 0) / currentPageTotal;
      const mediumRatio = (currentPageDifficulty.Medium || 0) / currentPageTotal;
      const hardRatio = (currentPageDifficulty.Hard || 0) / currentPageTotal;
      
      estimatedDifficulty.Easy = Math.round(currentTotal * easyRatio);
      estimatedDifficulty.Medium = Math.round(currentTotal * mediumRatio);
      estimatedDifficulty.Hard = Math.round(currentTotal * hardRatio);
      
      // Ensure the numbers add up to the total (handle rounding)
      const sum = estimatedDifficulty.Easy + estimatedDifficulty.Medium + estimatedDifficulty.Hard;
      if (sum !== currentTotal && sum > 0) {
        const diff = currentTotal - sum;
        // Add difference to the most common difficulty
        if (estimatedDifficulty.Medium >= estimatedDifficulty.Easy && estimatedDifficulty.Medium >= estimatedDifficulty.Hard) {
          estimatedDifficulty.Medium += diff;
        } else if (estimatedDifficulty.Easy >= estimatedDifficulty.Hard) {
          estimatedDifficulty.Easy += diff;
        } else {
          estimatedDifficulty.Hard += diff;
        }
      }
    }
    
    return {
      totalProblems: currentTotal,
      difficultyStats: estimatedDifficulty,
    };
  }, [stats, filters, pagination.total, problems, currentSearchQuery]);


  // Fetch initial data and stats
  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch initial problems
        const problemsResponse = await apiService.getProblems({
          page: 1,
          limit: 10,
        });
        setProblems(problemsResponse.data);
        setPagination({
          page: problemsResponse.page,
          limit: problemsResponse.limit,
          total: problemsResponse.total,
          hasNext: problemsResponse.hasNext,
          hasPrev: problemsResponse.hasPrev,
        });

        // Fetch stats
        const statsData = await apiService.getStats();
        setStats(statsData);
        setAvailableTags(statsData.topTags.map((t) => t.tag));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleMetaFilter = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSearchStatus('Searching for Meta problems...');

    // Update filters state to include company filter and clear search query
    setFilters(prev => ({ ...prev, company: "Meta" }));
    setCurrentSearchQuery(""); // Clear search query when using company filter

    try {
      const response = await apiService.getProblems({
        company: "Meta",
        page: 1,
        limit: pagination.limit,
        difficulty: filters.difficulty.length > 0 ? filters.difficulty : undefined,
        tags: filters.tags.length > 0 ? filters.tags : undefined,
      });

      setProblems(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        hasNext: response.hasNext,
        hasPrev: response.hasPrev,
      });

      setSearchStatus(
        response.total > 0
          ? `Found ${response.total} problem(s) from Meta`
          : "No problems found from Meta"
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Meta problems");
      setSearchStatus("");
    } finally {
      setLoading(false);
      // Clear status message after a few seconds
      setTimeout(() => setSearchStatus(""), 5000);
    }
  }, [pagination.limit, filters.difficulty, filters.tags]);

  const handleUnifiedSearch = useCallback(
    async (query: string, difficulty?: string) => {
      setLoading(true);
      setError(null);

      if (!query.trim()) {
        // Empty query - show all problems and clear company filter
        setSearchStatus("");
        setFilters(prev => ({ ...prev, company: "" }));
        setCurrentSearchQuery(""); // Clear search query
        try {
          const response = await apiService.getProblems({
            page: 1,
            limit: pagination.limit,
            difficulty:
              filters.difficulty.length > 0 ? filters.difficulty : undefined,
            tags: filters.tags.length > 0 ? filters.tags : undefined,
          });
          setProblems(response.data);
          setPagination((prev) => ({
            ...prev,
            page: response.page,
            total: response.total,
            hasNext: response.hasNext,
            hasPrev: response.hasPrev,
          }));
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Failed to load problems"
          );
        } finally {
          setLoading(false);
        }
        return;
      }

      // Declare timers outside try block for proper cleanup
      let searchModalTimer: NodeJS.Timeout | null = null;
      let generationTimer: NodeJS.Timeout | null = null;

      try {
        setSearchStatus(`Searching for "${query}"...`);
        
        // Clear company filter when doing a regular search but track the search query
        setFilters(prev => ({ ...prev, company: "" }));
        setCurrentSearchQuery(query); // Track current search query for stats

        // Debug: Force modal for testing if query contains "force modal"
        const forceModal = query.toLowerCase().includes('force modal');

        // Step 1: Initialize search modal state but don't show it yet
        setModalState({
          phase: 'searching',
          query: query,
          difficulty: difficulty as "Easy" | "Medium" | "Hard" | undefined
        });
        setIsModalOpen(false);

        const searchStartTime = Date.now();
        let searchModalShown = false;

        // Step 2: Set up timer to show search modal after 3 seconds
        const showSearchModal = () => {
          if (!searchModalShown) {
            console.log('⏰ Showing search modal after 3 seconds');
            setIsModalOpen(true);
            searchModalShown = true;
          }
        };
        searchModalTimer = setTimeout(showSearchModal, 3000);

        // Step 3: First API call - search for existing problems
        console.log('🔍 Starting search...');
        const searchResponse = await apiService.getProblems({
          search: query,
          page: 1,
          limit: pagination.limit,
          difficulty: filters.difficulty.length > 0 ? filters.difficulty : undefined,
          tags: filters.tags.length > 0 ? filters.tags : undefined,
        });

        const searchDuration = Date.now() - searchStartTime;
        console.log('🔍 Search completed:', { 
          query, 
          found: searchResponse.total, 
          duration: searchDuration,
          searchModalShown
        });

        // Step 4: Clear search timer and decide next action
        if (searchModalTimer) {
          clearTimeout(searchModalTimer);
        }

        if (searchResponse.total > 0 && !forceModal) {
          // Found existing problems - handle search modal
          console.log('✅ Found existing problems');
          
          if (searchModalShown) {
            // Search modal was shown - transition to success
            setModalState({
              phase: 'success',
              query: query,
              difficulty: difficulty as "Easy" | "Medium" | "Hard" | undefined,
              resultTitle: `Found ${searchResponse.total} existing problem(s)`
            });
          } else {
            // Fast search - don't show modal at all
            setIsModalOpen(false);
          }

          // Use search results
          setProblems(searchResponse.data);
          setPagination({
            page: searchResponse.page,
            limit: searchResponse.limit,
            total: searchResponse.total,
            hasNext: searchResponse.hasNext,
            hasPrev: searchResponse.hasPrev,
          });

        } else {
          // Step 5: No existing problems found - start generation
          console.log('🤖 No existing problems found, starting generation...');
          
          // Immediately switch to generation modal (replacing any search modal)
          setModalState({
            phase: 'generating',
            query: query,
            difficulty: difficulty as "Easy" | "Medium" | "Hard" | undefined,
            timeElapsed: searchDuration
          });
          setIsModalOpen(true);

          // Start generation progress timer
          const generationStartTime = Date.now();
          const updateGenerationProgress = () => {
            const elapsed = Date.now() - generationStartTime;
            setModalState(prev => ({
              ...prev,
              timeElapsed: searchDuration + elapsed
            }));
          };
          generationTimer = setInterval(updateGenerationProgress, 1000);

          // Step 6: Second API call - generate new problem
          // Pass difficulty only if specified; backend will determine appropriate difficulty otherwise
          const generateResponse = await apiService.generateProblem({
            topic: query,
            ...(difficulty && { difficulty: difficulty as "Easy" | "Medium" | "Hard" })
          });

          // Clear generation timer
          if (generationTimer) {
            clearInterval(generationTimer);
          }

          console.log('🎉 Problem generated:', generateResponse.title);

          // Show success modal
          setModalState({
            phase: 'success',
            query: query,
            difficulty: difficulty as "Easy" | "Medium" | "Hard" | undefined,
            resultTitle: generateResponse.title
          });

          // Use generated problem
          setProblems([generateResponse]);
          setPagination({
            page: 1,
            limit: pagination.limit,
            total: 1,
            hasNext: false,
            hasPrev: false,
          });

          // Update search status for generation
          setSearchStatus(
            `✅ Generated new ${difficulty} problem: "${generateResponse.title}"`
          );
          
          // Update stats after generation
          setTimeout(async () => {
            try {
              const updatedStats = await apiService.getStats();
              setStats(updatedStats);
              setAvailableTags(updatedStats.topTags.map((t) => t.tag));
            } catch (err) {
              console.error("Failed to update stats:", err);
            }
          }, 1000);
        }

        // Update search status for found problems
        if (searchResponse.total > 0 && !forceModal) {
          setSearchStatus(
            `Found ${searchResponse.total} existing problem(s) for "${query}"`
          );
        }

      } catch (err) {
        // Clear any pending timers
        if (searchModalTimer) clearTimeout(searchModalTimer);
        if (generationTimer) clearInterval(generationTimer);
        
        // Show error modal if modal was supposed to be shown
        setModalState({
          phase: 'error',
          query: query,
          difficulty: difficulty as "Easy" | "Medium" | "Hard" | undefined,
          message: err instanceof Error ? err.message : "Search failed"
        });
        setIsModalOpen(true);
        
        setError(err instanceof Error ? err.message : "Search failed");
        setSearchStatus("");
      } finally {
        setLoading(false);
        // Clear status message after a few seconds
        setTimeout(() => setSearchStatus(""), 5000);
      }
    },
    [pagination.limit, filters]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleDifficultyChange = useCallback((difficulty: string[]) => {
    setFilters((prev) => ({ ...prev, difficulty }));
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleTagsChange = useCallback((tags: string[]) => {
    setFilters((prev) => ({ ...prev, tags }));
  }, []);

  const handlePageChange = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const response = await apiService.getProblems({
          page,
          limit: pagination.limit,
          difficulty:
            filters.difficulty.length > 0 ? filters.difficulty : undefined,
          tags: filters.tags.length > 0 ? filters.tags : undefined,
          company: filters.company || undefined,
        });
        setProblems(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.page,
          hasNext: response.hasNext,
          hasPrev: response.hasPrev,
        }));
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load page");
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, filters]
  );

  const handlePageSizeChange = useCallback(
    async (limit: number) => {
      setPagination((prev) => ({ ...prev, limit, page: 1 }));
      // Trigger a new search with updated page size
      await handlePageChange(1);
    },
    [handlePageChange]
  );

  const startIndex = (pagination.page - 1) * pagination.limit;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);


  return (
    <div className="container">
      {/* Header */}
      <div className="header">
        <h1><AIPrepLogoIcon size={36} color="#6366f1" className="problems-header-icon" /> AIPrep - AI-Powered Coding Interview Prep</h1>
        <p>
          Search for problems or describe what you want - AI will find or create
          it!
        </p>
      </div>

      {/* Search Status */}
      {searchStatus && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            {searchStatus.includes("Searching") ||
            searchStatus.includes("Generating") ? (
              <div className="w-5 h-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
            ) : searchStatus.includes("✅") ? (
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            <span className="text-blue-800 font-medium">{searchStatus}</span>
          </div>
        </div>
      )}

      {/* Stats Dashboard */}
      {currentStats && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {currentStats.totalProblems}
              </div>
              <div className="text-sm text-gray-600">
                {filters.company 
                  ? `${filters.company} Problems` 
                  : currentSearchQuery.trim() 
                    ? `"${currentSearchQuery}" Results`
                    : 'Total Problems'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {currentStats.difficultyStats.Easy}
              </div>
              <div className="text-sm text-gray-600">Easy Problems</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {currentStats.difficultyStats.Medium}
              </div>
              <div className="text-sm text-gray-600">Medium Problems</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {currentStats.difficultyStats.Hard}
              </div>
              <div className="text-sm text-gray-600">Hard Problems</div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Search */}
      <div className="mb-8">
        <UnifiedSearchBar onSearch={handleUnifiedSearch} onMetaFilter={handleMetaFilter} isLoading={loading} />
      </div>


      {/* Active Company Filter Indicator */}
      {filters.company && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-green-800 font-medium">
                🏢 Filtering by company: {filters.company}
              </span>
            </div>
            <button
              onClick={() => {
                setFilters(prev => ({ ...prev, company: "" }));
                setCurrentSearchQuery(""); // Clear search query when clearing filter
                handleUnifiedSearch(""); // Reload all problems
              }}
              className="text-green-600 hover:text-green-800 text-sm font-medium"
            >
              Clear Filter ×
            </button>
          </div>
        </div>
      )}

      {/* Filters - Hidden */}
      {/* <div className="mb-6">
        <FilterBar
          selectedDifficulties={filters.difficulty}
          onDifficultyChange={handleDifficultyChange}
          selectedTags={filters.tags}
          onTagsChange={handleTagsChange}
          availableTags={availableTags}
        />
      </div> */}

      {/* Results Summary with Page Navigation and Page Size */}
      {!loading && !error && problems.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          {/* Left: Showing text */}
          <div className="text-sm text-gray-600 flex-1 text-left">
            Showing {startIndex + 1}-
            {Math.min(startIndex + pagination.limit, pagination.total)} of{" "}
            {pagination.total} problems
          </div>
          
          {/* Center: Pagination */}
          {totalPages > 1 && (
            <div className="flex-1 flex justify-center">
              <Pagination
                currentPage={pagination.page}
                totalPages={totalPages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={handlePageChange}
              />
            </div>
          )}
          
          {/* Right: Page size selector */}
          <div className="flex-1 flex justify-end">
            <PageSizeSelector
              currentSize={pagination.limit}
              onSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      )}

      {/* Problems List */}
      <ProblemList
        problems={problems}
        loading={loading}
        error={error}
        startIndex={startIndex}
        onRetry={() => handleUnifiedSearch("")}
      />


      {/* Summary Section */}
      <div className="summary-section">
        <h3><AIPrepLogoIcon size={24} color="white" /> AIPrep - Smart Problem Discovery</h3>
        <p>
          <strong>Intelligent Search & Generation:</strong>
        </p>
        <p>
          Find Existing Problems • Auto-Generate Missing Ones • Learn Patterns •
          Master Interviews
        </p>
      </div>

      {/* Search/Generation Modal */}
      <SearchGenerationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        state={modalState}
      />

    </div>
  );
};
